<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="380px" persistent>
    <v-card>
      <v-card-title>เปลี่ยนรหัสผ่าน</v-card-title>
      <v-card-text>
        <v-text-field label="ผู้ใช้งาน:" :value="who" color="orange accent-3" readonly></v-text-field>
        <v-text-field v-model="current" label="รหัสผ่านปัจจุบัน:" ref="current"
          :type="show ? 'text' : 'password'"
          :append-icon="show ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append="show = !show"
          :rules="[v => !!v || 'กรุณากรอกรหัสผ่านปัจจุบัน']"
          :error-messages="currentError"
          @input="currentError = ''"
          color="orange accent-3" autocomplete="current-password"></v-text-field>
        <v-text-field v-model="next" label="รหัสผ่านใหม่:" ref="next"
          :type="show ? 'text' : 'password'"
          :rules="nextRules"
          color="orange accent-3" autocomplete="new-password"></v-text-field>
        <v-text-field v-model="confirm" label="ยืนยันรหัสผ่านใหม่:" ref="confirm"
          :type="show ? 'text' : 'password'"
          :rules="[v => v === next || 'รหัสผ่านไม่ตรงกัน']"
          color="orange accent-3" autocomplete="new-password"
          @keydown.enter="save"></v-text-field>
        <div class="caption grey--text text--darken-1">
          อุปกรณ์อื่นที่เข้าระบบด้วยบัญชีนี้อยู่จะต้องเข้าสู่ระบบใหม่
        </div>
        <v-alert v-if="error" type="error" dense text class="mt-3 mb-0">{{ error }}</v-alert>
      </v-card-text>
      <v-card-actions>
        <v-layout>
          <v-flex text-right mr-1>
            <v-btn @click="save" color="green" dark :loading="saving">ตกลง</v-btn>
          </v-flex>
          <v-flex text-left ml-1>
            <v-btn color="red" @click="close" dark tabindex="-1" :disabled="saving">ยกเลิก</v-btn>
          </v-flex>
        </v-layout>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// A signed-in staff or shop user changes their own password.
//
// Entirely client-side: Firebase Auth lets a user change their own password, but
// only just after proving who they are, so the current password is checked with
// reauthenticateWithCredential before updatePassword. That check is also what
// stops a passer-by at a signed-in shop PC from taking the account over.
//
// Never offered to a driver -- mainMenu does not list it for that role. A driver
// has a PIN, not a password; see resetDriverPin in functions/index.js for why a
// driver account must not hold one anyone knows.
import * as firebase from 'firebase/app'
import { auth } from '@/firebase'

// Firebase Auth's own minimum, and the one resetUserPassword enforces.
const MIN_LENGTH = 6

const ERRORS = {
  'auth/too-many-requests': 'ลองรหัสผ่านผิดหลายครั้งเกินไป กรุณารอสักครู่แล้วลองใหม่',
  'auth/weak-password': `รหัสผ่านใหม่ต้องมีอย่างน้อย ${MIN_LENGTH} ตัวอักษร`,
  'auth/network-request-failed': 'เชื่อมต่อระบบไม่ได้ โปรดลองใหม่',
  'auth/user-disabled': 'บัญชีนี้ถูกระงับการใช้งาน',
  'auth/user-token-expired': 'หมดเวลาการเข้าสู่ระบบ โปรดเข้าสู่ระบบใหม่แล้วลองอีกครั้ง'
}

export default {
  props: {
    value: { type: Boolean, default: false }
  },
  data () {
    return {
      current: '',
      next: '',
      confirm: '',
      show: false,
      saving: false,
      error: '',
      currentError: '',
      nextRules: [
        v => !!v || 'กรุณากรอกรหัสผ่านใหม่',
        v => (v || '').length >= MIN_LENGTH || `รหัสผ่านต้องมีอย่างน้อย ${MIN_LENGTH} ตัวอักษร`,
        v => v !== this.current || 'รหัสผ่านใหม่ต้องไม่ซ้ำกับรหัสผ่านปัจจุบัน'
      ]
    }
  },
  computed: {
    // auth.currentUser is not reactive, so `value` is read here to make the
    // dialog re-read it each time it opens rather than keep a cached address.
    email () {
      return (this.value && auth.currentUser && auth.currentUser.email) || ''
    },
    // What the person signs in with -- the user ID for a user-ID account, whose
    // email is not an address they know -- and their name. Re-authentication
    // below still uses the real email.
    who () {
      if (!this.email) return ''
      const m = /^([^@]+)@users\.delivery-kiosk\.local$/.exec(this.email)
      const login = m ? m[1] : this.email
      const name = this.$personName(this.email)
      return name === this.email ? login : `${login} (${name})`
    }
  },
  watch: {
    value (open) {
      if (!open) return
      this.reset()
      this.$nextTick(() => this.$refs.current && this.$refs.current.focus())
    }
  },
  methods: {
    reset () {
      this.current = ''
      this.next = ''
      this.confirm = ''
      this.show = false
      this.error = ''
      this.currentError = ''
      ;['current', 'next', 'confirm'].forEach(r => this.$refs[r] && this.$refs[r].resetValidation())
    },

    close () {
      this.reset()
      this.$emit('input', false)
    },

    async save () {
      if (this.saving) return
      this.error = ''
      for (const r of ['current', 'next', 'confirm']) {
        if (!this.$refs[r].validate(true)) {
          this.$refs[r].focus()
          return
        }
      }
      const user = auth.currentUser
      if (!user || !user.email) {
        this.error = 'ไม่พบการเข้าสู่ระบบ โปรดเข้าสู่ระบบใหม่'
        return
      }

      this.saving = true
      try {
        const credential = firebase.auth.EmailAuthProvider.credential(user.email, this.current)
        await user.reauthenticateWithCredential(credential)
        await user.updatePassword(this.next)
        this.close()
        this.$emit('changed')
      } catch (e) {
        const code = e && e.code
        if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
          this.currentError = 'รหัสผ่านปัจจุบันไม่ถูกต้อง'
          this.$nextTick(() => this.$refs.current.focus())
        } else {
          this.error = ERRORS[code] || 'เปลี่ยนรหัสผ่านไม่สำเร็จ โปรดลองใหม่'
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
